import GoogleIcon from '@mui/icons-material/Google';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { OtpField } from '@/components/forms';
import {
  GoogleSignInCancelledError,
  useGoogleOAuth,
} from '@/features/identity/hooks/useGoogleOAuth';
import { useToast } from '@/hooks/useToast';
import { authService } from '@/services/authService';
import type { OAuthProvider } from '@/types/auth.types';
import { clientOrServerMessage, parseRestError } from '@/utils/errorUtils';

interface ResetMfaValues {
  otp: string;
}

export interface ResetMfaDialogProps {
  open: boolean;
  /** True when the account may switch MFA off for good; false when it may only re-bind a device. */
  canTurnOffCompletely: boolean;
  /** What the user chose: re-bind to a new authenticator, or stop using MFA entirely. */
  intent: 'rebind' | 'disable';
  /**
   * Whether this account has a local password (`CurrentUser.hasPassword`). When false, the first stage
   * must NOT ask for one — a provider-created account has no password to confirm (BR-8).
   */
  hasPassword: boolean;
  /** The account's linked sign-in providers (`CurrentUser.linkedOAuthProviders`). */
  linkedProviders: OAuthProvider[];
  onClose: () => void;
  /** Fired once MFA is off. `rebind` callers open enrollment straight away. */
  onReset: (intent: 'rebind' | 'disable') => void;
}

/**
 * Removes MFA from the caller's own account after re-authenticating on two factors that do NOT
 * involve the authenticator itself — because the whole point is that the authenticator is gone.
 *
 * <p>Stage 1 re-proves who the caller is with whatever credential the account actually has: its
 * password, or a fresh sign-in with the linked provider. Stage 2 takes a code emailed to the verified
 * address. Stage 1 alone is deliberately insufficient: if it were enough, stealing a password — or a
 * live session — would silently strip MFA and the control would be decorative exactly when it matters
 * most.</p>
 *
 * <p><b>The stage-1 branch is not cosmetic.</b> An account created through Google has no password at
 * all, so the old unconditional "confirm your password" prompt was unanswerable for those users and the
 * server refused them outright — sending them to support for something they are entitled to do
 * themselves.</p>
 *
 * <p>Completing this revokes every session server-side, so the user is signed out everywhere — the
 * lost device included.</p>
 */
export function ResetMfaDialog({
  open,
  canTurnOffCompletely,
  intent,
  hasPassword,
  linkedProviders,
  onClose,
  onReset,
}: ResetMfaDialogProps) {
  const [stage, setStage] = useState<'reauth' | 'otp'>('reauth');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { requestAccessToken, isConfigured: googleConfigured } = useGoogleOAuth();
  const { control, watch, reset: resetForm } = useForm<ResetMfaValues>({
    defaultValues: { otp: '' },
  });
  const otp = watch('otp');

  const turnOffCompletely = intent === 'disable' && canTurnOffCompletely;

  // Password first when the account has one: a user who knows their password should not be sent to a
  // provider popup for no reason. Google is the fallback, and the only provider surfaced in the client
  // today (see SocialLoginButtons) — an account with neither has no self-service route at all.
  const method: 'password' | 'google' | 'none' = hasPassword
    ? 'password'
    : linkedProviders.includes('Google')
      ? 'google'
      : 'none';

  const close = () => {
    setStage('reauth');
    setPassword('');
    resetForm({ otp: '' });
    setError(null);
    onClose();
  };

  /** Shared tail of both stage-1 paths: the server has emailed a code, so move on to spending it. */
  const codeSent = () => {
    setStage('otp');
    toast.showInfo('We emailed you a reset code.');
  };

  const submitPassword = async () => {
    setBusy(true);
    setError(null);
    try {
      await authService.requestMfaReset({ password });
      codeSent();
    } catch (err) {
      setError(parseRestError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const submitGoogle = async () => {
    setBusy(true);
    setError(null);
    try {
      const accessToken = await requestAccessToken();
      await authService.requestMfaReset({ oAuthProvider: 'Google', oAuthAccessToken: accessToken });
      codeSent();
    } catch (err) {
      // Dismissing the popup is a choice, not a failure — drop back to the button with no error shown.
      if (!(err instanceof GoogleSignInCancelledError)) {
        setError(clientOrServerMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async () => {
    setBusy(true);
    setError(null);
    try {
      await authService.confirmMfaReset({ otp, turnOffCompletely });
      onReset(intent);
      close();
    } catch (err) {
      setError(parseRestError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="xs" fullWidth>
      <DialogTitle>
        {intent === 'disable' ? 'Turn off multi-factor authentication' : 'Set up a new device'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {stage === 'reauth' ? (
            <>
              {method === 'password' ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    Confirm your password. We&apos;ll email a one-time code to finish. Your
                    authenticator app is not needed.
                  </Typography>
                  <TextField
                    type="password"
                    label="Current password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    autoComplete="current-password"
                  />
                </>
              ) : null}

              {method === 'google' ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    This account uses Google sign-in. Re-authenticate with Google to continue.
                    We&apos;ll then email a one-time code, and your authenticator app is not needed.
                  </Typography>
                  <Tooltip
                    title={
                      googleConfigured ? '' : 'Google sign-in is not configured in this environment'
                    }
                  >
                    <span>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<GoogleIcon />}
                        disabled={!googleConfigured || busy}
                        onClick={() => void submitGoogle()}
                        sx={{ minHeight: 44 }}
                      >
                        Continue with Google
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : null}

              {/* Neither a password nor a linked provider this client can drive. Says so instead of
                  showing a button that cannot work — the server would refuse it anyway. */}
              {method === 'none' ? (
                <Alert severity="info">
                  We can&apos;t confirm your identity automatically for this account. Contact support
                  to remove multi-factor authentication.
                </Alert>
              ) : null}
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Enter the 6-digit code we emailed you.{' '}
                {turnOffCompletely
                  ? 'Multi-factor authentication will be turned off.'
                  : 'Your current authenticator will stop working and you can set up a new one.'}{' '}
                You&apos;ll be signed out on every device.
              </Typography>
              <OtpField name="otp" control={control} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close} disabled={busy} sx={{ minHeight: 44 }}>
          Cancel
        </Button>
        {stage === 'reauth' ? (
          // The Google path's action is the provider button in the body, so there is no second
          // submit here — a "Continue" that did nothing would be worse than none.
          method === 'password' ? (
            <Button
              variant="contained"
              onClick={() => void submitPassword()}
              disabled={busy || password.length === 0}
              sx={{ minHeight: 44 }}
            >
              Email me a code
            </Button>
          ) : null
        ) : (
          <Button
            variant="contained"
            color={turnOffCompletely ? 'error' : 'primary'}
            onClick={() => void submitOtp()}
            disabled={busy || otp.length !== 6}
            sx={{ minHeight: 44 }}
          >
            {turnOffCompletely ? 'Turn off MFA' : 'Reset authenticator'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
